import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { User, Shield } from "lucide-react";

// Small memoized cache to avoid refetching the same avatar multiple times per page
const cache = new Map();

export default function Avatar({ player, size = 44, className = "" }) {
  const [src, setSrc] = useState(null);
  const hasAvatar = !!player?.has_avatar;
  const id = player?.id;

  useEffect(() => {
    if (!hasAvatar || !id) {
      setSrc(null);
      return;
    }
    if (cache.has(id)) {
      setSrc(cache.get(id));
      return;
    }
    let alive = true;
    let localUrl = null;
    (async () => {
      try {
        const res = await fetch(`${API}/players/${id}/avatar`, { credentials: "include" });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!alive) return;
        localUrl = URL.createObjectURL(blob);
        cache.set(id, localUrl);
        setSrc(localUrl);
      } catch (e) {}
    })();
    return () => {
      alive = false;
    };
  }, [id, hasAvatar]);

  const isCoach = player?.role === "coach" || player?.role === "admin";

  if (src) {
    return (
      <img
        src={src}
        alt={player?.name || ""}
        className={`rounded-lg object-cover ${className}`}
        style={{ width: size, height: size }}
        data-testid={`avatar-${id}`}
      />
    );
  }
  return (
    <div
      className={`rounded-lg bg-slate-800 flex items-center justify-center font-mono-stat font-bold ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(12, size / 3) }}
      data-testid={`avatar-fallback-${id}`}
    >
      {player?.jersey_number || (isCoach ? <Shield className="w-1/2 h-1/2 text-[#E5B85C]" /> : <User className="w-1/2 h-1/2 text-slate-500" />)}
    </div>
  );
}

export function invalidateAvatar(id) {
  const url = cache.get(id);
  if (url) URL.revokeObjectURL(url);
  cache.delete(id);
}
